import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  getIdToken
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

require('firebase/auth');

const firebaseConfig = {
  apiKey: "AIzaSyDaPbyDUxHDL-YK70zZfGKdq5fhJgxSXXA",
  authDomain: "cario-58145.firebaseapp.com",
  projectId: "cario-58145",
  storageBucket: "cario-58145.appspot.com",
  messagingSenderId: "120822014022",
  appId: "1:120822014022:web:2c4568b1d399fee8c8ea47",
  measurementId: "G-07W4E06JS1"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

export { app, db, auth, storage, serverTimestamp };


export const getToken = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        getIdToken(user).then(
          (idToken) => {
            resolve(idToken);
          },
          (error) => {
            resolve(null);
          }
        );
      } else {
        resolve(null);
      }
    });
  });
};

export const token = async () => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const idToken = await currentUser.getIdToken();
      return idToken;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

// Mensajes de error de Firebase Auth en castellano
const AUTH_ERRORS = {
  "auth/invalid-credential": "El email o la contraseña no son correctos.",
  "auth/wrong-password": "El email o la contraseña no son correctos.",
  "auth/user-not-found": "El email o la contraseña no son correctos.",
  "auth/invalid-email": "El email no es válido.",
  "auth/email-already-in-use": "Ya existe una cuenta con ese email. Iniciá sesión o continuá con Google.",
  "auth/weak-password": "La contraseña tiene que tener al menos 6 caracteres.",
  "auth/too-many-requests": "Demasiados intentos. Esperá unos minutos y probá de nuevo.",
  "auth/network-request-failed": "No hay conexión. Revisá tu internet y probá de nuevo.",
  "auth/user-disabled": "Esta cuenta está deshabilitada.",
  "auth/account-exists-with-different-credential":
    "Ya tenés una cuenta con ese email. Iniciá sesión con tu contraseña.",
  "auth/popup-closed-by-user": "",
  "auth/cancelled-popup-request": "",
};

export const authErrorMessage = (error) => {
  const message = AUTH_ERRORS[error?.code];
  if (message !== undefined) return message;
  return error?.message && !error.message.startsWith("Firebase")
    ? error.message
    : "No pudimos completar la operación. Probá de nuevo.";
};

export const signInUser = (email, password) => {
  const auth = getAuth();
  return setPersistence(auth, browserLocalPersistence).then(() => {
    return signInWithEmailAndPassword(auth, email, password);
  });
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw new Error(
      "Error al enviar el correo electrónico para restablecer la contraseña: " +
        error.message
    );
  }
};

const DEFAULT_PROFILE_IMAGE =
  "https://firebasestorage.googleapis.com/v0/b/tripdio.appspot.com/o/user.png?alt=media&token=0ab01826-e0d2-430b-8dfd-3cab3070aa34";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

// Crea los datos del usuario la primera vez que entra (por ejemplo, con Google)
export const ensureUserProfile = async (user) => {
  const userRef = doc(db, "users", user.uid);
  const snapshot = await getDoc(userRef);
  if (snapshot.exists()) return;
  const photoURL = user.photoURL || DEFAULT_PROFILE_IMAGE;
  await setDoc(userRef, {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || "",
    phone: user.phoneNumber || "",
    point: "",
    profileImageUrl: photoURL,
  });
  await syncPublicProfile(user.uid, { displayName: user.displayName || "", photoURL });
};

// Guarda los datos pedidos en el primer ingreso con Google
export const completeUserProfile = async (user, { displayName, phone, point }) => {
  const userRef = doc(db, "users", user.uid);
  const current = (await getDoc(userRef)).data() || {};
  const photoURL = current.profileImageUrl || user.photoURL || DEFAULT_PROFILE_IMAGE;
  await setDoc(
    userRef,
    { uid: user.uid, email: user.email || "", displayName, phone, point, profileImageUrl: photoURL },
    { merge: true }
  );
  await syncPublicProfile(user.uid, { displayName, photoURL });
};

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

// Ventana emergente de Google; si el navegador la bloquea, redirige a Google y vuelve
export const signInWithGoogle = async () => {
  await setPersistence(auth, browserLocalPersistence);
  try {
    const { user } = await signInWithPopup(auth, googleProvider);
    await ensureUserProfile(user);
    return user;
  } catch (error) {
    if (error.code === "auth/popup-blocked" || error.code === "auth/operation-not-supported-in-this-environment") {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw error;
  }
};

// Completa el ingreso cuando se volvió de la redirección de Google
export const completeGoogleRedirect = async () => {
  const result = await getRedirectResult(auth);
  if (!result?.user) return null;
  await ensureUserProfile(result.user);
  return result.user;
};

// Perfil visible para otros usuarios (en los chats): sólo nombre y foto.
// Los datos de contacto quedan en "users", que sólo puede leer su dueño.
export const syncPublicProfile = async (uid, { displayName, photoURL }) => {
  const profile = {};
  if (displayName !== undefined) profile.displayName = (displayName || "").slice(0, 80);
  if (photoURL !== undefined) profile.photoURL = photoURL || "";
  if (Object.keys(profile).length === 0) return;
  await setDoc(doc(db, "publicProfiles", uid), profile, { merge: true });
};

export const createUser = async (
  email,
  password,
  displayName,
  phone,
  point,
) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  const uid = user.uid;

  await setDoc(doc(db, "users", uid), {
    uid: uid,
    email: email,
    displayName: displayName,
    phone: phone,
    point: point,
    profileImageUrl: DEFAULT_PROFILE_IMAGE,
  });
  await syncPublicProfile(uid, { displayName, photoURL: DEFAULT_PROFILE_IMAGE });
  await sendEmailVerification(user);

  return userCredential;
};

export const updateUser = async (uid, displayName, phone, point) => {
  const userRef = doc(db, "users", uid);
  // merge: crea el documento si la cuenta todavía no lo tiene
  await setDoc(userRef, { uid, displayName, phone, point }, { merge: true });
  await syncPublicProfile(uid, { displayName });
  const userData = await getDoc(userRef);
  return { uid, ...userData.data() };
};

export const uploadImage = async (file) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Tenés que iniciar sesión para subir imágenes.");
  if (!file.type.startsWith("image/")) throw new Error("Sólo se pueden subir imágenes.");
  if (file.size > MAX_IMAGE_SIZE) throw new Error("La imagen supera los 10 MB.");

  const extension = (file.name.split(".").pop() || "jpg").replace(/[^a-z0-9]/gi, "").slice(0, 5);
  const storageRef = ref(storage, `images/${user.uid}/${crypto.randomUUID()}.${extension}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
};

export const updateUserProfile = async (uid, profileData) => {
  await setDoc(doc(db, "users", uid), { uid, ...profileData }, { merge: true });
  if (profileData.profileImageUrl !== undefined) {
    await syncPublicProfile(uid, { photoURL: profileData.profileImageUrl });
  }
};
